import type { HintVerdict, PurchaseHint, Store } from '@recipes/contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StarRating,
  Switch,
  Textarea,
  cn,
} from '@recipes/ui';
import { Ban, Pencil, StickyNote, ThumbsUp, Trash2 } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import { toastError } from '@/lib/errors';
import { VerdictBadge } from './badges';
import { createStoreLocal, deleteHint, saveHint, updateHint } from './catalog-queue';
import { useCatalogStores } from './hooks';
import { storeLabel } from './store-label';

const NO_STORE = '__none__';
const NEW_STORE = '__new__';
const VERDICTS: Array<{ value: HintVerdict; icon: typeof Ban }> = [
  { value: 'RECOMMENDED', icon: ThumbsUp },
  { value: 'AVOID', icon: Ban },
  { value: 'NOTE', icon: StickyNote },
];

export interface HintTarget {
  ingredientId?: string | null;
  productId?: string | null;
  name: string;
}

/**
 * Create or edit a purchase hint (docs/finished/ingredient-catalog-spec.md 5.4). Writes go to the
 * device first and are queued, so the dialog works at the shelf without a connection.
 */
export function HintDialog({
  open,
  onOpenChange,
  target,
  hint,
  defaultStoreId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: HintTarget;
  hint?: PurchaseHint | null;
  defaultStoreId?: string | null;
}) {
  const { t } = useTranslation();
  const ids = { store: useId(), text: useId(), newStore: useId(), onlyMe: useId() };
  const stores = useCatalogStores();
  const [verdict, setVerdict] = useState<HintVerdict>('AVOID');
  const [rating, setRating] = useState<number | null>(null);
  const [text, setText] = useState('');
  const [storeId, setStoreId] = useState<string>(NO_STORE);
  const [newStore, setNewStore] = useState('');
  const [onlyMe, setOnlyMe] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setVerdict(hint?.verdict ?? 'AVOID');
    setRating(hint?.rating ?? null);
    setText(hint?.text ?? '');
    setStoreId(hint ? (hint.storeId ?? NO_STORE) : (defaultStoreId ?? NO_STORE));
    setNewStore('');
    setOnlyMe(false);
  }, [open, hint, defaultStoreId]);

  const valid =
    (verdict !== 'NOTE' || text.trim().length > 0) &&
    (storeId !== NEW_STORE || newStore.trim().length > 0);

  const submit = async () => {
    setSaving(true);
    try {
      let store: Store | null = stores.find((s) => s.id === storeId) ?? null;
      if (storeId === NEW_STORE)
        store = await createStoreLocal({
          name: newStore.trim(),
          kind: 'CHAIN',
          ...(onlyMe ? { householdId: null } : {}),
        });
      if (hint) {
        await updateHint(hint, {
          verdict,
          rating,
          text: text.trim(),
          storeId: store?.id ?? null,
          storeName: store?.name ?? null,
        });
      } else {
        await saveHint({
          ...(onlyMe ? { householdId: null } : {}),
          ingredientId: target.ingredientId ?? null,
          productId: target.productId ?? null,
          storeId: store?.id ?? null,
          storeName: store?.name ?? null,
          targetName: target.name,
          verdict,
          rating,
          text: text.trim(),
        });
      }
      toast.success(t('catalog.hint.saved'));
      onOpenChange(false);
    } catch (error) {
      toastError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{hint ? t('catalog.hint.edit') : t('catalog.hint.add')}</DialogTitle>
          <DialogDescription>{t('catalog.hint.about', { name: target.name })}</DialogDescription>
        </DialogHeader>
        <form
          noValidate
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) void submit();
          }}
        >
          <fieldset>
            <legend className="mb-1 text-sm font-medium">{t('catalog.hint.verdict')}</legend>
            <div className="grid grid-cols-3 gap-2" role="radiogroup">
              {VERDICTS.map(({ value, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={verdict === value}
                  onClick={() => setVerdict(value)}
                  data-testid={`hint-verdict-${value}`}
                  className={cn(
                    'flex min-h-[44px] flex-col items-center justify-center gap-1 rounded-md border px-2 py-2 text-xs font-medium',
                    verdict === value ? 'border-primary bg-primary/10' : 'hover:bg-accent/40',
                  )}
                >
                  <Icon className="size-4" aria-hidden />
                  {t(`catalog.verdict.${value}`)}
                </button>
              ))}
            </div>
          </fieldset>

          <div>
            <Label htmlFor={ids.store}>{t('catalog.hint.store')}</Label>
            <Select value={storeId} onValueChange={setStoreId}>
              <SelectTrigger id={ids.store} className="mt-1" data-testid="hint-store">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_STORE}>{t('catalog.hint.everywhere')}</SelectItem>
                {stores.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {storeLabel(s)}
                  </SelectItem>
                ))}
                <SelectItem value={NEW_STORE}>{t('catalog.hint.newStore')}</SelectItem>
              </SelectContent>
            </Select>
            {storeId === NEW_STORE && (
              <div className="mt-2">
                <Label htmlFor={ids.newStore} className="sr-only">
                  {t('catalog.hint.newStoreName')}
                </Label>
                <Input
                  id={ids.newStore}
                  value={newStore}
                  maxLength={160}
                  placeholder={t('catalog.hint.newStoreName')}
                  onChange={(e) => setNewStore(e.target.value)}
                  data-testid="hint-new-store"
                />
              </div>
            )}
          </div>

          <div>
            <Label htmlFor={ids.text}>
              {t('catalog.hint.text')}
              {verdict !== 'NOTE' && (
                <span className="ml-1 font-normal text-muted-foreground">
                  ({t('common.optional')})
                </span>
              )}
            </Label>
            <Textarea
              id={ids.text}
              className="mt-1"
              rows={3}
              maxLength={1000}
              value={text}
              placeholder={t('catalog.hint.textPlaceholder')}
              onChange={(e) => setText(e.target.value)}
              data-testid="hint-text"
            />
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm font-medium">{t('catalog.hint.rating')}</span>
            <StarRating
              value={rating}
              allowHalf={false}
              label={t('catalog.hint.rating')}
              onChange={(value) => setRating(value === rating ? null : value)}
            />
          </div>

          {!hint && (
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor={ids.onlyMe} className="font-normal">
                {t('catalog.hint.onlyMe')}
              </Label>
              <Switch id={ids.onlyMe} checked={onlyMe} onCheckedChange={setOnlyMe} />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={saving} disabled={!valid} data-testid="hint-save">
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Hints with author and store; own hints (and the household's, for editors) can be changed. */
export function HintList({
  hints,
  target,
  pendingIds,
  emptyText,
}: {
  hints: PurchaseHint[];
  target: HintTarget;
  pendingIds?: Set<string>;
  emptyText?: string;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<PurchaseHint | null>(null);
  if (hints.length === 0)
    return emptyText ? <p className="text-sm text-muted-foreground">{emptyText}</p> : null;
  return (
    <>
      <ul className="space-y-2" data-testid="hint-list">
        {hints.map((hint) => (
          <li key={hint.id} className="rounded-md border p-2 text-sm" data-testid="hint">
            <div className="flex flex-wrap items-center gap-2">
              <VerdictBadge verdict={hint.verdict} />
              {hint.storeName && (
                <span className="text-xs font-medium">
                  {t('catalog.hint.atStore', { store: hint.storeName })}
                </span>
              )}
              {hint.productId === null && target.productId && hint.targetName && (
                <span className="text-xs text-muted-foreground">{hint.targetName}</span>
              )}
              {hint.rating !== null && (
                <span className="text-xs text-muted-foreground">
                  {t('catalog.hint.stars', { count: hint.rating })}
                </span>
              )}
              {pendingIds?.has(hint.id) && (
                <span className="text-xs text-muted-foreground">{t('catalog.pendingSync')}</span>
              )}
              {hint.own && (
                <span className="ml-auto flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('catalog.hint.edit')}
                    onClick={() => setEditing(hint)}
                  >
                    <Pencil aria-hidden />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('catalog.hint.delete')}
                    onClick={() => void deleteHint(hint.id).catch(toastError)}
                  >
                    <Trash2 aria-hidden />
                  </Button>
                </span>
              )}
            </div>
            {hint.text && <p className="mt-1">{hint.text}</p>}
            {hint.authorName && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t('catalog.hint.by', { name: hint.authorName })}
              </p>
            )}
          </li>
        ))}
      </ul>
      <HintDialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        target={target}
        hint={editing}
      />
    </>
  );
}
